import React, { useCallback, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { array, number, func, string } from 'prop-types';
import { translate, Trans } from 'react-i18next';
import classnames from 'classnames/bind';

import { isMobile } from '@snapask/common-components/dist/utils/detectMobileType';
import { XMarkIcon } from '@snapask/common-components';
import TokenDropdownItem from '../TokenDropdownItem';

import { getPriceUnitString } from '@dragonfruit/utils/priceUnit';
import { quotaParser } from '@dragonfruit/utils/quotaParser';
import { RENEWABLE } from '@dragonfruit/constants/planTypes';

import styles from './TokenDropdown.scss';
const cx = classnames.bind(styles);

function TokenDropdown({ value, options, onChangeHandler, className }) {
  const getOptionValue = useCallback(option => option.price, []);

  const getOption = useCallback(
    targetValue => {
      // when the value can't find in options
      const [firstValidOption] = options.filter(option =>
        Boolean(option.price)
      );
      return (
        options.find(option => getOptionValue(option) === targetValue) ||
        firstValidOption
      );
    },
    [getOptionValue, options]
  );

  const [selectOption, setSelectOption] = useState(getOption(value));
  const [showOptionList, setShowOptionList] = useState(false);
  const optionListRef = useRef(null);

  useEffect(() => {
    onChangeHandler(selectOption);
  }, [selectOption, onChangeHandler]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const handleClickOutside = useCallback(
    event => {
      if (
        optionListRef.current &&
        !optionListRef.current.contains(event.target)
      ) {
        setShowOptionList(false);
      }
    },
    [setShowOptionList]
  );

  const toggleShowOptionList = useCallback(
    () => setShowOptionList(showOptionList => !showOptionList),
    []
  );

  const handleClickOption = option => () => {
    const currentSelectedOption =
      getOptionValue(option) === getOptionValue(selectOption);

    if (currentSelectedOption) return;

    setSelectOption(option);
    setShowOptionList(false);
  };

  const renderDropdownListComponent = mobile => (
    <div className={cx(styles.wrapper, { active: showOptionList, mobile })}>
      <ul ref={optionListRef} className={cx(styles.inner)}>
        <div className={styles.header}>
          <XMarkIcon width={24} height={24} onClick={toggleShowOptionList} />
        </div>
        {options.map((option, optionIdx) => (
          <TokenDropdownItem
            {...option}
            selected={getOptionValue(option) === getOptionValue(selectOption)}
            handleClick={handleClickOption(option)}
            key={optionIdx}
          >
            {renderQuotaInfo(option)}
          </TokenDropdownItem>
        ))}
      </ul>
    </div>
  );

  const DropdownListComponent = () =>
    isMobile() && showOptionList
      ? createPortal(
          renderDropdownListComponent(true),
          document.getElementById('modal')
        )
      : renderDropdownListComponent(false);

  const renderQuotaInfo = plan => {
    const {
      quota,
      planType,
      billingCyclePerMonths,
      contractPeriod,
      isContract,
      price,
      currency,
      displayCurrency,
    } = plan;

    const isRenewable = planType === RENEWABLE;

    const { displayPriceUnit } = getPriceUnitString({
      billingCyclePerMonths,
      contractPeriod,
      isContract,
      price,
      currency,
      displayCurrency,
    });

    return (
      <div className={styles.quota}>
        <Trans
          i18nKey={
            quota > 1
              ? 'premium.quotaUnit.qaWithCount_plural'
              : 'premium.quotaUnit.qaWithCount'
          }
          count={quota}
        >
          Quota
          <span>{{ count: quotaParser(quota) }}</span>
        </Trans>
        {isRenewable && (
          <span className={styles.billingCycleUnit}>{displayPriceUnit}</span>
        )}
      </div>
    );
  };

  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.placeholder} onClick={toggleShowOptionList}>
        {renderQuotaInfo(selectOption)}
        <span className={styles.arrowIcon} />
      </div>
      <DropdownListComponent />
    </div>
  );
}

TokenDropdown.propTypes = {
  options: array.isRequired,
  t: func.isRequired,
  value: number.isRequired,
  onChangeHandler: func.isRequired,
  className: string,
};

TokenDropdown.defaultProps = {
  className: '',
};

export default translate(['premium'])(TokenDropdown);



// WEBPACK FOOTER //
// ./app/containers/Premium/components/TokenPack/components/TokenDropdown/TokenDropdown.jsx